import React, { useReducer, useContext, useEffect, useRef, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { modifierGroupReducer } from './modifierGroupReducer';
import {
	IGetPaginatedModifierGroupsV2,
	IModifierGroupsV2,
	SortByForPaginatedModifierGroups,
	DirectionForPaginated,
} from 'types';
import { PRE_SELECTED_CUSTOMER_ID_FOR_MENU } from 'appConstants';
import { ModifierGroupActionTypes } from './modifierGroupsActions';
import {
	modifierGroupInitialState,
	ModifierGroupContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './modifierGroupContext';
import { getPaginatedModifierGroupsV2, deleteModifierGroupsV2 } from 'backend/modifierGroups';
import { AxiosResponse } from 'axios';
import { useFetchData } from 'hooks/useFetchData';

export const ModifierGroupState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(modifierGroupReducer, modifierGroupInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<
		AxiosResponse<IModifierGroupsV2>,
		IGetPaginatedModifierGroupsV2
	>(isMounted.current && state.IDSelectedCustomer ? getPaginatedModifierGroupsV2 : null, {
		customerId: state.IDSelectedCustomer || '',
		restaurantId: state.IDSelectedRestaurant || null,
		pageNumber: state.currentPage,
		pageSize: Number(state.countOfItems),
		searchInput: state.searchString ? state.searchString : null,
		sorting: state.sortBy
			? {
					sortBy: {
						'ModifierGroupsA-Z': SortByForPaginatedModifierGroups.Name,
						'ModifierGroupsZ-A': SortByForPaginatedModifierGroups.Name,
					}[state.sortBy],
					direction: {
						'ModifierGroupsA-Z': DirectionForPaginated.Ascending,
						'ModifierGroupsZ-A': DirectionForPaginated.Descending,
					}[state.sortBy],
			  }
			: null,
	});

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			const transformedData = {
				modifierGroups: response.data.items.map((item) => ({
					id: item.id,
					name: item.name,
					articleNumber: item.articleNumber,
					customerId: state.IDSelectedCustomer || '',
					customerName: null,
					restaurantsModifierGroups: item.restaurants.map((restaurant) => ({
						id: restaurant.id,
						restaurantId: restaurant.id,
						restaurantName: restaurant.name,
					})),
				})),
				modifierGroupsCount: response.data.totalCount,
			};
			dispatch({
				type: ModifierGroupActionTypes.SET_MODIFIER_GROUPS,
				payload: {
					modifierGroups: transformedData,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: ModifierGroupActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	const refetchData = (value: boolean) => {
		dispatch({
			type: ModifierGroupActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	};

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteModifierGroupsV2(state.IDForDeletion);
			const filteredModifierGroups = state.modifierGroups.modifierGroups.filter(
				(el) => el.id !== state.IDForDeletion,
			);
			dispatch({
				type: ModifierGroupActionTypes.SET_MODIFIER_GROUPS,
				payload: {
					modifierGroups: {
						modifierGroupsCount: state.modifierGroups.modifierGroupsCount - 1,
						modifierGroups: filteredModifierGroups,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: ModifierGroupActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: ModifierGroupActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const toggleRememberMyChoice = () => {
		!state.rememberMyChoice && state.IDSelectedCustomer
			? localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, state.IDSelectedCustomer)
			: localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);

		dispatch({
			type: ModifierGroupActionTypes.TOGGLE_REMEMBER_MY_CHOICE,
			payload: {
				rememberMyChoice: !state.rememberMyChoice,
			},
		});
	};

	const setIDCustomer = (value: Nullable<string>) => {
		value && state.rememberMyChoice
			? localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, value)
			: localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);

		dispatch({
			type: ModifierGroupActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value ? value : null,
				modifierGroups: value ? state.modifierGroups : { modifierGroupsCount: 0, modifierGroups: [] },
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: ModifierGroupActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value ? value : null,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: ModifierGroupActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TSortBy) => {
		dispatch({
			type: ModifierGroupActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TCountOfItems) => {
		dispatch({
			type: ModifierGroupActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: ModifierGroupActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: ModifierGroupActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForAssignRestaurants = (value: string) => {
		dispatch({
			type: ModifierGroupActionTypes.SET_ID_FOR_ASSIGN_RESTAURANTS,
			payload: {
				IDForAssignRestaurants: value,
				typeOfModalWindow: value ? 'assignRestaurants' : null,
			},
		});
	};

	const setIDForEditRestaurantProperties = (value: string) => {
		dispatch({
			type: ModifierGroupActionTypes.SET_ID_FOR_EDIT_RESTAURANT_PROPERTIES,
			payload: {
				IDForEditRestaurantProperties: value,
				typeOfModalWindow: value ? 'editRestaurantProperties' : null,
			},
		});
	};

	const setIDForEditModifiersTemplate = (value: string) => {
		dispatch({
			type: ModifierGroupActionTypes.SET_ID_FOR_EDIT_MODIFIERS_TEMPLATE,
			payload: {
				IDForEditModifiersTemplate: value,
				typeOfModalWindow: value ? 'editModifiersTemplate' : null,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: ModifierGroupActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		!isMounted.current &&
			value.IDSelectedCustomer &&
			localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, value.IDSelectedCustomer);

		dispatch({
			type: ModifierGroupActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: {
					...value,
					IDSelectedCustomer:
						value.IDSelectedCustomer || localStorage.getItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU),
				},
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<ModifierGroupContext.Provider
			value={{
				refetchData,
				deleteSelectedItem,
				changeSearchString,
				toggleRememberMyChoice,
				setIDCustomer,
				setIDRestaurant,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForAssignRestaurants,
				setIDForEditRestaurantProperties,
				setIDForEditModifiersTemplate,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				needRefetchData: state.needRefetchData,
				modifierGroups: state.modifierGroups,
				searchString: state.searchString,
				rememberMyChoice: state.rememberMyChoice,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForAssignRestaurants: state.IDForAssignRestaurants,
				IDForEditRestaurantProperties: state.IDForEditRestaurantProperties,
				IDForEditModifiersTemplate: state.IDForEditModifiersTemplate,
				isInitializedSelectCustomerModal: state.isInitializedSelectCustomerModal,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ModifierGroupContext.Provider>
	);
};
